// Copied from konradwalz.com/fallstudie.
export type CaseStudy = {
  id: string;
  title: string;
  lead: string;
  client?: string;
  /** Stand-in visuals (AI-generated) — replace with the original project images. */
  image: string;
  imageAlt: string;
  intro: string[];
  steps: { heading: string; text: string }[];
  info: { label: string; values: string[] }[];
  closing: { heading: string; text: string };
  meta: string[];
};

const defaultMeta = ['2 Wochen', 'Deutschland / Schweiz / Österreich', 'Auswahl an CAD-Programmen', '3D-CAD + Zeichnungen'];

export const caseStudies: CaseStudy[] = [
  {
    id: 'irisblende',
    title: 'Von der Idee zum fertigen Produkt',
    lead: 'Idee → Entwicklung → Konstruktion → fertiges Produkt',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5160a4d3-b14d-4c5c-a2aa-598e260f808a.jpg',
    imageAlt: '3D-druckbare Irisblende mit ca. 250 mm Durchmesser',
    intro: [
      'Aus einer ersten Idee und wenigen Vorgaben entstand eine vollständig konstruierte 3D-druckbare Irisblende mit ca. 250 mm Durchmesser.',
      'Von der Anforderungsdefinition über die mechanische Entwicklung und CAD-Konstruktion bis hin zur Baugruppenprüfung und fertigungsgerechten Ausarbeitung wurden alle Schritte umgesetzt.',
      'Das Ergebnis: vollständige CAD-Daten, Einzelteile, technische Zeichnungen und 3D-druckfähige Dateien – bereit für Fertigung und Montage.',
    ],
    steps: [],
    info: [],
    closing: { heading: '', text: '' },
    meta: [],
  },
  {
    id: 'kartonskizze',
    title: 'Aus einer Kartonskizze zur fertigen CAD-Baugruppe',
    lead: 'Ein Foto. Ein Telefonat. Daraus entstand eine vollständige Baugruppe.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/685e8632-737b-4442-8949-b1d99b3898db.jpg',
    imageAlt: 'Handskizze auf Karton neben dem fertigen 3D-CAD-Modell der Baugruppe',
    intro: [
      'Nicht jedes Projekt beginnt mit einem fertigen Lastenheft oder einer bestehenden Konstruktion.',
      'Bei diesem Projekt bestand die gesamte Ausgangslage lediglich aus einer handgezeichneten Skizze auf einem Stück Karton. Es existierten weder CAD-Daten noch technische Zeichnungen oder detaillierte Fertigungsunterlagen.',
      'Die einzige Grundlage waren ein Foto der Handskizze sowie mehrere telefonische Abstimmungen.',
    ],
    steps: [
      {
        heading: 'Ausgangssituation',
        text: 'Für den Umbau einer Komponente einer Kläranlage sollte eine vollständig neue Baugruppe entwickelt werden. Da keine digitalen Unterlagen vorhanden waren, musste die Konstruktion direkt aus der Handskizze interpretiert und Schritt für Schritt in ein präzises 3D-CAD-Modell überführt werden.',
      },
      {
        heading: 'Herausforderung',
        text: 'Die größte Herausforderung bestand darin, aus sehr wenigen Informationen eine technisch schlüssige Konstruktion zu entwickeln. Viele geometrische Zusammenhänge mussten logisch abgeleitet werden, obwohl keine vollständigen technischen Unterlagen existierten.',
      },
      {
        heading: 'Umsetzung',
        text: 'Auf Grundlage des Fotos der Kartonskizze wurde zunächst die Grundgeometrie entwickelt. Während der Konstruktion wurden sämtliche Komponenten aufgebaut, miteinander abgestimmt und kontinuierlich anhand telefonischer Rückmeldungen ergänzt. Besonderes Augenmerk lag auf einer logisch aufgebauten Baugruppenstruktur, sodass zukünftige Änderungen problemlos möglich bleiben.',
      },
      {
        heading: 'Ergebnis',
        text: 'Aus einer einfachen Handskizze entstand innerhalb kurzer Zeit eine vollständig bearbeitbare CAD-Baugruppe. Der Kunde erhielt eine digitale Grundlage, auf der zukünftige Änderungen und technische Weiterentwicklungen effizient umgesetzt werden können.',
      },
    ],
    info: [
      { label: 'Branche', values: ['Abwassertechnik'] },
      { label: 'Leistung', values: ['CAD-Konstruktion', 'Baugruppenentwicklung', 'Digitalisierung von Handskizzen'] },
      { label: 'Software', values: ['Autodesk Inventor'] },
      { label: 'Lieferumfang', values: ['Vollständiges 3D-CAD-Modell', 'Baugruppenstruktur', 'Bearbeitbare CAD-Daten'] },
    ],
    closing: {
      heading: 'Sie verfügen ebenfalls nur über Skizzen oder Fotos?',
      text: 'Nicht jedes Projekt beginnt mit perfekten Unterlagen. Oft reichen bereits wenige Informationen aus, um eine technische Lösung zu entwickeln.',
    },
    meta: defaultMeta,
  },
  {
    id: 'motorsportgetriebe',
    title: 'Reverse Engineering eines geradeverzahnten Motorsportgetriebes',
    lead: 'Keine Zeichnungen. Keine CAD-Daten. Nur Fotos.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6bc150ac-5428-43f2-a7be-2859d3f813c8.jpg',
    imageAlt: 'CAD-Rekonstruktion eines geradeverzahnten Motorsportgetriebes in Explosionsansicht',
    client: 'Kunde Haertl: „Das Seriengetriebe ist viel zu kurz und litt unter Zahnausfall.“',
    intro: [
      'Für dieses Projekt standen keinerlei CAD-Modelle oder technische Zeichnungen zur Verfügung. Die komplette Rekonstruktion musste ausschließlich anhand mehrerer Fotos eines Bauteils eines Alpine A110 Rallye-Fahrzeugs erfolgen.',
    ],
    steps: [
      {
        heading: 'Ausgangssituation',
        text: 'Das Ziel bestand darin, ein vorhandenes Bauteil vollständig digital nachzubilden. Da ausschließlich Fotos vorlagen, mussten sämtliche Proportionen, Geometrien und Anschlussflächen sorgfältig interpretiert und rekonstruiert werden.',
      },
      {
        heading: 'Herausforderung',
        text: 'Fotos liefern keine exakten Maße. Perspektiven, Blickwinkel und optische Verzerrungen erschweren die Rekonstruktion erheblich. Das Bauteil musste dennoch möglichst realitätsnah aufgebaut werden und später als Grundlage für weitere Entwicklungsarbeiten dienen.',
      },
      {
        heading: 'Umsetzung',
        text: 'Zunächst wurden sämtliche verfügbaren Bilder analysiert und miteinander verglichen. Anschließend entstand Schritt für Schritt ein vollständig bearbeitbares CAD-Modell. Während der Modellierung wurden sämtliche Geometrien kontinuierlich überprüft und an die erkannten Proportionen angepasst.',
      },
      {
        heading: 'Ergebnis',
        text: 'Aus mehreren Fotografien entstand ein vollständiges digitales CAD-Modell. Die Rekonstruktion bildet die Grundlage für spätere technische Änderungen, Visualisierungen oder weitere Entwicklungsarbeiten.',
      },
    ],
    info: [
      { label: 'Branche', values: ['Motorsport'] },
      { label: 'Leistung', values: ['Reverse Engineering', 'CAD-Konstruktion'] },
      { label: 'Software', values: ['Autodesk Inventor'] },
      { label: 'Lieferumfang', values: ['Bearbeitbares CAD-Modell', 'Rekonstruktion anhand von Fotos', 'Technische Modellierung'] },
    ],
    closing: {
      heading: 'Reverse Engineering beginnt häufig genau hier.',
      text: 'Nicht mit perfekten Daten. Sondern mit Fotos, vorhandenen Bauteilen oder wenigen Informationen.',
    },
    meta: defaultMeta,
  },
  {
    id: 'altbauplaene',
    title: 'Historische Baupläne digitalisiert – von Papier zu AutoCAD',
    lead: '40 Jahre alte Papierzeichnungen wurden wieder digital nutzbar.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/83ecf8c1-e8db-4145-bd3e-8a73f4d2c981.jpg',
    imageAlt: 'Historischer Papier-Bauplan neben der digitalisierten AutoCAD-Zeichnung',
    client: 'Digitalisierung von Altbauplänen – Houseport Immobilien (CH)',
    intro: [
      'Für ein Wohngebäude in Bern existierten ausschließlich Baupläne aus den 1980er Jahren. Für zukünftige Umbauten und Planungen wurden jedoch vollständig digitale CAD-Daten benötigt.',
    ],
    steps: [
      {
        heading: 'Ausgangssituation',
        text: 'Die vorhandenen Papierzeichnungen mussten vollständig digitalisiert werden. Dabei sollten Grundrisse, Ansichten und Schnitte präzise übernommen und als saubere CAD-Dateien aufgebaut werden.',
      },
      {
        heading: 'Herausforderung',
        text: 'Historische Pläne enthalten häufig handschriftliche Ergänzungen, unterschiedliche Maßangaben oder kleinere Ungenauigkeiten. Alle Informationen mussten sorgfältig geprüft und konsistent in moderne CAD-Zeichnungen übertragen werden.',
      },
      {
        heading: 'Umsetzung',
        text: 'Alle Baupläne wurden analysiert und anschließend vollständig in AutoCAD neu aufgebaut. Während der Digitalisierung wurden sämtliche Zeichnungen strukturiert organisiert und aufeinander abgestimmt. So entstand ein vollständiger digitaler Zeichnungssatz.',
      },
      {
        heading: 'Ergebnis',
        text: 'Die ursprünglichen Papierzeichnungen wurden vollständig in moderne CAD-Daten überführt. Der Kunde erhielt strukturierte DWG-Dateien sowie PDF-Pläne als Grundlage für zukünftige Umbauten und weitere Planungsleistungen.',
      },
    ],
    info: [
      { label: 'Branche', values: ['Architektur / Bestandsgebäude'] },
      { label: 'Leistung', values: ['Digitalisierung historischer Baupläne'] },
      { label: 'Software', values: ['AutoCAD'] },
      { label: 'Lieferumfang', values: ['DWG-Dateien', 'PDF-Pläne', 'Grundrisse', 'Ansichten', 'Schnitte'] },
    ],
    closing: {
      heading: 'Papierpläne müssen kein Hindernis sein.',
      text: 'Auch ältere Zeichnungen lassen sich in moderne CAD-Daten überführen und für zukünftige Projekte nutzbar machen.',
    },
    meta: defaultMeta,
  },
];
